/**
 * Configuración de entorno validada y tipada.
 *
 * Todo el código de la aplicación consume `env` en lugar de `import.meta.env`
 * directamente: así los valores por defecto y las validaciones viven en un solo
 * lugar y los errores de configuración aparecen al arrancar, no en runtime.
 */

function readString(key: keyof ImportMetaEnv, fallback: string): string {
  const raw = import.meta.env[key];
  if (typeof raw !== 'string' || raw.trim() === '') return fallback;
  return raw.trim();
}

function readInt(key: keyof ImportMetaEnv, fallback: number): number {
  const parsed = Number.parseInt(readString(key, String(fallback)), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function readUrl(key: keyof ImportMetaEnv, fallback: string): string {
  const value = readString(key, fallback).replace(/\/+$/, '');

  try {
    // Valida el formato sin depender del objeto URL global en entornos exóticos.
    void new URL(value);
  } catch {
    console.warn(`[env] ${key} no es una URL válida ("${value}"). Se usa ${fallback}.`);
    return fallback.replace(/\/+$/, '');
  }

  return value;
}

/**
 * URL base de la API.
 *
 * - Valor con esquema (`https://api...`): el frontend llama directo a ese host.
 * - Cadena vacía: se usa el mismo origen y el proxy de desarrollo de Vite
 *   (`server.proxy`) reenvía `/api` a EDSuite API. Es el modo recomendado en
 *   desarrollo: sin CORS y compatible con entornos remotos.
 * - Sin definir: se asume EDSuite API en el puerto 9000 local.
 */
function readApiUrl(): string {
  const raw = import.meta.env.VITE_API_URL;

  if (raw === undefined) return 'http://localhost:9000';

  const value = raw.trim().replace(/\/+$/, '');
  if (value === '') return '';

  return readUrl('VITE_API_URL', 'http://localhost:9000');
}

export const env = {
  /**
   * URL base de EDSuite API.
   * Cadena vacía = mismo origen (proxy de desarrollo de Vite).
   */
  apiUrl: readApiUrl(),

  /** Nombre comercial mostrado en la interfaz. */
  appName: readString('VITE_APP_NAME', 'EDSuite'),

  /** Timeout por defecto de cada request (ms). */
  apiTimeout: readInt('VITE_API_TIMEOUT', 20_000),

  /** Espera antes de reintentar una request con el token renovado (ms). */
  apiRetryDelay: readInt('VITE_API_RETRY_DELAY', 150),

  /**
   * URL base del panel anterior (CodeIgniter). Vacío = no se ofrecen enlaces al
   * panel legado desde EDSuite Web.
   */
  legacyUrl: readString('VITE_LEGACY_URL', '').replace(/\/+$/, ''),

  /**
   * URL del **portal del cliente**.
   *
   * El portal es una aplicación aparte (`edsuite-portal`), así que el panel no lo
   * sirve: se usa para enviar a los clientes que entren por la dirección antigua
   * (`/portal-cliente`) y para los enlaces que apunten allí. Vacío = no publicada.
   */
  portalUrl: readString('VITE_PORTAL_URL', '').replace(/\/+$/, ''),

  /** Atajos de entorno. */
  isDev: import.meta.env.DEV,
  isProd: import.meta.env.PROD,
  mode: import.meta.env.MODE,
} as const;

export type AppEnv = typeof env;
